import { createDailyCheckin } from '../lib/chat';
import { ChatModal, injectChatModalStyles } from './ChatModal';

export class DailyCheckinModal {
  private overlay: HTMLElement;
  private modal: HTMLElement;
  private userId: string;
  private chatModal: ChatModal;
  private selectedAnxiety: number | null = null;

  constructor(userId: string) {
    this.userId = userId;
    this.chatModal = new ChatModal(userId);
    this.overlay = this.createOverlay();
    this.modal = this.createModal();
    this.setupEventListeners();

    // Inject chat modal styles
    injectChatModalStyles();
  }

  private createOverlay(): HTMLElement {
    const overlay = document.createElement('div');
    overlay.id = 'daily-checkin-overlay';
    overlay.className = 'daily-checkin-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.7);
      z-index: 10000;
      display: none;
      align-items: center;
      justify-content: center;
      backdrop-filter: blur(8px);
    `;
    document.body.appendChild(overlay);
    return overlay;
  }

  private createModal(): HTMLElement {
    const modal = document.createElement('div');
    modal.id = 'daily-checkin-modal';
    modal.className = 'daily-checkin-modal';
    modal.innerHTML = `
      <div class="daily-checkin-content">
        <div class="daily-checkin-character">
          <svg width="120" height="120" viewBox="0 0 32 32" style="image-rendering: pixelated;">
            <rect x="8" y="4" width="16" height="24" fill="#FFB6C1"/>
            <rect x="10" y="8" width="4" height="4" fill="#000"/>
            <rect x="18" y="8" width="4" height="4" fill="#000"/>
            <rect x="12" y="16" width="8" height="2" fill="#000"/>
          </svg>
        </div>

        <h2 class="daily-checkin-title">Hey there! 👋</h2>
        <p class="daily-checkin-text">How are you feeling today?</p>

        <div class="daily-checkin-scale">
          <div class="scale-label">Anxiety Level</div>
          <div class="scale-buttons">
            ${[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(level => `
              <button class="anxiety-level-btn" data-level="${level}">
                <span class="level-number">${level}</span>
                <span class="level-emoji">${this.getAnxietyEmoji(level)}</span>
              </button>
            `).join('')}
          </div>
          <div class="scale-hints">
            <span>Calm</span>
            <span>High</span>
          </div>
        </div>

        <button id="checkin-continue-btn" class="checkin-continue-btn" disabled>
          Continue →
        </button>
      </div>
    `;

    modal.style.cssText = `
      background: #fff;
      border: 3px solid #000;
      border-radius: 24px;
      box-shadow: 0 12px 0 #000;
      padding: 40px;
      max-width: 500px;
      width: 90%;
      animation: slideUp 0.3s ease;
    `;

    this.overlay.appendChild(modal);
    return modal;
  }

  private getAnxietyEmoji(level: number): string {
    if (level <= 2) return '😌';
    if (level <= 4) return '🙂';
    if (level <= 6) return '😐';
    if (level <= 8) return '😰';
    return '😱';
  }

  private setupEventListeners(): void {
    // Anxiety level buttons
    const levelButtons = this.modal.querySelectorAll('.anxiety-level-btn');
    levelButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const button = e.currentTarget as HTMLElement;
        const level = parseInt(button.dataset.level || '0');
        this.selectAnxietyLevel(level);
      });
    });

    // Continue button
    const continueBtn = this.modal.querySelector('#checkin-continue-btn') as HTMLButtonElement;
    continueBtn.addEventListener('click', () => this.handleContinue());
  }

  private selectAnxietyLevel(level: number): void {
    this.selectedAnxiety = level;

    // Update UI
    const levelButtons = this.modal.querySelectorAll('.anxiety-level-btn');
    levelButtons.forEach(btn => {
      const button = btn as HTMLElement;
      if (parseInt(button.dataset.level || '0') === level) {
        button.classList.add('selected');
      } else {
        button.classList.remove('selected');
      }
    });

    // Enable continue button
    const continueBtn = this.modal.querySelector('#checkin-continue-btn') as HTMLButtonElement;
    continueBtn.disabled = false;
  }

  private async handleContinue(): Promise<void> {
    if (this.selectedAnxiety === null) return;

    // Disable button and show loading
    const continueBtn = this.modal.querySelector('#checkin-continue-btn') as HTMLButtonElement;
    const originalText = continueBtn.textContent;
    continueBtn.disabled = true;
    continueBtn.textContent = 'Creating conversation...';

    // Create check-in and conversation
    const result = await createDailyCheckin(this.userId, this.selectedAnxiety);

    if (result) {
      // Close this modal
      this.close();

      // Open chat modal with the new conversation and initial prompt
      const title = this.selectedAnxiety >= 7
        ? 'Daily Check-in - Let\'s calm things down'
        : 'Daily Check-in';

      this.chatModal.open(result.conversation.id, title);

      // Send initial message to guide the conversation
      const initialPrompt = this.selectedAnxiety >= 7
        ? `I'm feeling pretty anxious right now (${this.selectedAnxiety}/10). I could use some help calming down.`
        : `I'm checking in for today. My anxiety level is ${this.selectedAnxiety}/10.`;

      // The chat modal will handle sending this and getting AI response
      setTimeout(() => {
        const chatInput = document.getElementById('chat-modal-input') as HTMLTextAreaElement;
        const sendBtn = document.getElementById('chat-modal-send') as HTMLButtonElement;
        if (chatInput && sendBtn) {
          chatInput.value = initialPrompt;
          sendBtn.click();
        }
      }, 300);

      // Store the conversation ID for later
      localStorage.setItem('daily_checkin_conversation_id', result.conversation.id);
    } else {
      // Error handling
      continueBtn.disabled = false;
      continueBtn.textContent = originalText || 'Continue →';
      alert('Sorry, there was an error creating your check-in. Please try again.');
    }
  }

  public open(): void {
    this.overlay.style.display = 'flex';
    this.selectedAnxiety = null;

    // Reset UI
    const levelButtons = this.modal.querySelectorAll('.anxiety-level-btn');
    levelButtons.forEach(btn => btn.classList.remove('selected'));

    const continueBtn = this.modal.querySelector('#checkin-continue-btn') as HTMLButtonElement;
    continueBtn.disabled = true;
    continueBtn.textContent = 'Continue →';
  }

  public close(): void {
    this.overlay.style.display = 'none';
  }

  public destroy(): void {
    this.overlay.remove();
    this.chatModal.destroy();
  }
}

/**
 * Add required styles to the document
 */
export function injectDailyCheckinStyles(): void {
  if (document.getElementById('daily-checkin-styles')) return;

  const style = document.createElement('style');
  style.id = 'daily-checkin-styles';
  style.textContent = `
    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .daily-checkin-content {
      text-align: center;
    }

    .daily-checkin-character {
      margin: 0 auto 24px;
      width: 120px;
      height: 120px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .daily-checkin-title {
      font-size: 28px;
      font-weight: 700;
      margin: 0 0 12px;
      color: var(--text-color, #000);
    }

    .daily-checkin-text {
      font-size: 18px;
      color: var(--text-muted, #666);
      margin: 0 0 32px;
    }

    .daily-checkin-scale {
      margin-bottom: 32px;
    }

    .scale-label {
      font-size: 14px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--text-muted, #666);
      margin-bottom: 16px;
    }

    .scale-buttons {
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      gap: 8px;
      margin-bottom: 12px;
    }

    .anxiety-level-btn {
      background: #fff;
      border: 2px solid #e0e0e0;
      border-radius: 12px;
      padding: 12px 8px;
      cursor: pointer;
      transition: all 0.2s ease;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      font-family: inherit;
    }

    .anxiety-level-btn:hover {
      border-color: #3DDAB4;
      background: rgba(61, 218, 180, 0.05);
      transform: translateY(-2px);
    }

    .anxiety-level-btn.selected {
      border-color: #3DDAB4;
      background: #3DDAB4;
      color: #0b1f18;
      box-shadow: 0 4px 0 #000;
    }

    .level-number {
      font-size: 14px;
      font-weight: 700;
    }

    .level-emoji {
      font-size: 20px;
    }

    .scale-hints {
      display: flex;
      justify-content: space-between;
      font-size: 12px;
      color: var(--text-muted, #666);
      padding: 0 4px;
    }

    .checkin-continue-btn {
      width: 100%;
      padding: 16px 32px;
      background: #3DDAB4;
      color: #0b1f18;
      border: 2px solid #000;
      border-radius: 999px;
      font-size: 16px;
      font-weight: 700;
      cursor: pointer;
      box-shadow: 0 6px 0 #000;
      transition: all 0.08s ease;
      font-family: inherit;
    }

    .checkin-continue-btn:hover:not(:disabled) {
      transform: translateY(2px);
      box-shadow: 0 4px 0 #000;
      background: #2cb895;
    }

    .checkin-continue-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    @media (max-width: 600px) {
      .scale-buttons {
        grid-template-columns: repeat(5, 1fr);
        gap: 6px;
      }

      .anxiety-level-btn {
        padding: 8px 4px;
      }

      .level-number {
        font-size: 12px;
      }

      .level-emoji {
        font-size: 16px;
      }
    }
  `;

  document.head.appendChild(style);
}
